// ChatView — Phase 2: real RPC + SSE wiring.
//
// State now lives in the useChat hook; this component just composes the
// shell (sidebar + main + actions menu) and forwards user actions down.
// Mocked threads/messages are gone; everything flows through
// /api/rpc + the SSE event stream on /api/events.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import "../../styles/chat.css";

import { Icon } from "../../icons";
import type { Me } from "../../lib/use-me";
import type { Tweaks } from "../../lib/use-tweaks";
import { useChat } from "../../lib/use-chat";
import { useEnv } from "../../lib/use-env";
import { api, type SessionSearchHit } from "../../lib/rpc";

import { ChatSidebar } from "./sidebar";
import { ChatComposer } from "./composer";
import { ChatActionsMenu } from "./actions-menu";
import { EmptyChat, Message, ThinkingBubble } from "./messages";
import { ApprovalCard, AskCard } from "./agent-prompts";
import { ToolCallGroup, ReasoningRow } from "./tool-call";
import { ShareModal } from "./share-modal";
import { SkillsPanel } from "./skills-panel";
import { MemoryPanel } from "./memory-panel";
import { PresetsPanel } from "./presets-panel";
import { MOCK_SUGGESTIONS } from "./mock";
import { confirm } from "../../components/confirm";

const CHAT_MODES = ["default", "plan", "acceptEdits"] as const;
type ChatMode = (typeof CHAT_MODES)[number];

// User-facing labels for each mode. Internal IDs stay as
// default/plan/acceptEdits because the server's permission check
// expects those exact strings. Only the display changes.
export const MODE_LABEL: Record<ChatMode, string> = {
  default:     "Ask",
  plan:        "Plan",
  acceptEdits: "Auto",
};
export const MODE_DESCRIPTION: Record<ChatMode, string> = {
  default:     "Confirm before doing anything",
  plan:        "Talk it through, don't act yet",
  acceptEdits: "Skip prompts, just go",
};

interface ChatViewProps {
  me: Me | null;
  tweaks: Tweaks;
  notify: (msg: string) => void;
  /** Optional message handed in from Today's QuickChat. We auto-send
   *  it on mount and call `onPendingConsumed` so it doesn't re-fire on
   *  re-render. */
  pendingMessage?: string | null;
  onPendingConsumed?: () => void;
}

export function ChatView({ me, tweaks, notify, pendingMessage, onPendingConsumed }: ChatViewProps) {
  const chat = useChat();
  const env = useEnv();
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [mode, setMode] = useState<ChatMode>("default");

  const current = useMemo(
    () => chat.threads.find((t) => t.id === chat.active) ?? null,
    [chat.threads, chat.active],
  );

  // Filter the visible threads by search.
  const filteredThreads = useMemo(() => {
    if (!search) return chat.threads;
    const q = search.toLowerCase();
    return chat.threads.filter((t) =>
      (t.name || "").toLowerCase().includes(q) ||
      (t.preview || "").toLowerCase().includes(q),
    );
  }, [chat.threads, search]);

  // Content-match search — fires the semantic RPC (with FTS fallback)
  // when the user types 2+ chars. Debounced so each keystroke doesn't
  // round-trip to the rerank model. Cleared instantly on empty input
  // so going from "rice" → "" doesn't flash stale hits.
  const [contentHits, setContentHits] = useState<SessionSearchHit[]>([]);
  const [contentSearching, setContentSearching] = useState(false);
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setContentHits([]);
      setContentSearching(false);
      return;
    }
    let cancelled = false;
    setContentSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const hits = await api.sessionSearch(q, 25);
        if (!cancelled) setContentHits(hits);
      } catch {
        if (!cancelled) setContentHits([]);
      } finally {
        if (!cancelled) setContentSearching(false);
      }
    }, 220);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [search]);

  // Pending message-index scroll target — set when the user clicks a
  // content hit, consumed by the messages effect below once the chat
  // loads. Ref instead of state so a quick second click doesn't race
  // against a render commit.
  const pendingScrollIdxRef = useRef<number | null>(null);
  const openThreadAtIdx = (id: string, opts?: { scrollToIdx?: number; excerpt?: string }) => {
    const targetIdx = opts?.scrollToIdx;
    const sameThread = id === chat.active;
    if (typeof targetIdx === "number") pendingScrollIdxRef.current = targetIdx;
    pendingExcerptRef.current = opts?.excerpt || null;
    chat.setActive(id);
    setSideOpen(false);
    // Same-thread click: chat.messages won't change, so the bottom-
    // scroll effect won't fire — kick the flash off ourselves on the
    // next frame so the matched message gets surfaced anyway.
    if (sameThread && typeof targetIdx === "number") {
      requestAnimationFrame(() => {
        if (flashAtIdx(targetIdx)) {
          pendingScrollIdxRef.current = null;
          pendingExcerptRef.current = null;
        }
      });
    }
  };

  // Surface errors as toasts so the user actually notices.
  useEffect(() => {
    if (chat.error) notify(chat.error);
  }, [chat.error, notify]);

  // Consume a pending message handed in from Today's QuickChat. We
  // route this through a two-step dance to avoid a UI flicker where
  // the most-recent thread paints briefly before the new turn lands:
  //   1. Stash the text in a ref + force `active` to null so the
  //      ChatView immediately shows the empty "new conversation"
  //      shell instead of the auto-selected most-recent thread.
  //   2. When the next render confirms `active === null`, fire
  //      `chat.send`. send then mints a fresh session id so the
  //      message goes to its own brand-new thread.
  const pendingChatRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingMessage) return;
    pendingChatRef.current = pendingMessage;
    onPendingConsumed?.();
    chat.setActive(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMessage]);

  useEffect(() => {
    if (chat.active === null && pendingChatRef.current) {
      const text = pendingChatRef.current;
      pendingChatRef.current = null;
      chat.send(text, mode, []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.active]);

  const messagesRef = useRef<HTMLDivElement>(null);

  // Pending excerpt fallback — when openThreadAtIdx hands us a target
  // index that's beyond the loaded message count (typical after a
  // server-side compaction collapsed the older turns), the next-best
  // anchor is *any* visible message whose text contains a recognisable
  // chunk of the hit excerpt. Stored alongside the idx ref so the
  // effect below can consume both atomically.
  const pendingExcerptRef = useRef<string | null>(null);

  // Scroll-and-flash the matched message bubble. Tries the exact idx
  // first, falls back to a text-search over visible bubbles when the
  // idx is out of range. Returns true on success so callers can decide
  // whether to fall back to bottom-scroll.
  const flashAtIdx = (idx: number): boolean => {
    if (!messagesRef.current) return false;
    let row = messagesRef.current.querySelector<HTMLElement>(`[data-msg-idx="${idx}"]`);
    if (!row && pendingExcerptRef.current) {
      // Strip the <mark>…</mark> tags + entity escapes the server
      // wrapped around matched tokens, then pull the longest plain
      // word ≥ 4 chars as the anchor to match against bubble text.
      const plain = pendingExcerptRef.current
        .replace(/<\/?mark>/g, "")
        .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"').replace(/&#x27;/g, "'");
      const tokens = plain.match(/[A-Za-z0-9]{4,}/g) || [];
      // Prefer the matched tokens (originally between <mark> tags) but
      // fall back to anything from the excerpt — gives us a hit even
      // when the highlight word didn't survive into the bubble text.
      const marked = (pendingExcerptRef.current.match(/<mark>(.*?)<\/mark>/g) || [])
        .map((t) => t.replace(/<\/?mark>/g, ""));
      const candidates = [...marked, ...tokens].filter((t) => t.length >= 4);
      const bubbles = Array.from(
        messagesRef.current.querySelectorAll<HTMLElement>(".msg[data-msg-idx] .bubble")
      );
      for (const tok of candidates) {
        const lower = tok.toLowerCase();
        const match = bubbles.find((b) => b.innerText.toLowerCase().includes(lower));
        if (match) {
          row = match.closest<HTMLElement>(".msg[data-msg-idx]");
          if (row) break;
        }
      }
    }
    if (!row) return false;
    row.scrollIntoView({ behavior: "auto", block: "center" });
    row.classList.add("msg-hit-flash");
    window.setTimeout(() => row.classList.remove("msg-hit-flash"), 1600);
    return true;
  };

  // Auto-scroll on any visual change in the message stream: new
  // messages, thinking-state, or a new agent prompt card sliding in at
  // the bottom. Without the prompt deps, an approval card that drops
  // below a long conversation would land off-screen.
  // EXCEPT: when the user came from a content-hit click, we want to
  // land on the matched message instead of jumping to the bottom. The
  // pendingScrollIdxRef is consumed once when the messages array
  // updates, then cleared.
  useEffect(() => {
    if (!messagesRef.current) return;
    const targetIdx = pendingScrollIdxRef.current;
    // Try the flash any time we have a pending target, even when the
    // exact idx is past chat.messages.length — flashAtIdx will fall
    // back to the excerpt-text search in that case. Only when both
    // routes fail do we fall through to bottom-scroll.
    if (targetIdx != null && chat.messages.length > 0) {
      if (flashAtIdx(targetIdx)) {
        pendingScrollIdxRef.current = null;
        pendingExcerptRef.current = null;
        return;
      }
    }
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [chat.messages, chat.thinking, chat.pendingApproval, chat.pendingQuestion, chat.toolCalls, chat.compacting, chat.compactionMarkers]);

  const openThread = (id: string) => {
    chat.setActive(id);
    setSideOpen(false);
  };

  const onSend = (
    attachments: import("../../lib/use-chat").Attachment[],
    explicitText?: string,
  ) => {
    // explicitText is supplied by the auto-send path (mic dictation
    // commits the transcript and triggers send in the same beat — the
    // setDraft inside the composer hasn't flushed to our state yet,
    // so we'd send the empty draft if we relied on it here).
    const trimmed = (explicitText ?? draft).trim();
    if (chat.sending) return;
    if (!trimmed && attachments.length === 0) return;
    setDraft("");
    chat.send(trimmed, mode, attachments);
  };

  // Title + meta for the header. Falls back to a "New conversation"
  // label when the user has clicked + to start a fresh thread but
  // hasn't sent anything yet (no server-side session exists).
  const headerTitle = current?.name || (chat.active === null ? "New conversation" : "Untitled");
  const headerUpdated = current ? formatRelative(current.updatedAt) : "";

  return (
    <div
      className="chat-shell"
      data-dense={tweaks.sidebarDense}
      data-side-open={sideOpen}
    >
      {sideOpen && (
        <div className="chat-side-backdrop" onClick={() => setSideOpen(false)} />
      )}

      <ChatSidebar
        threads={filteredThreads}
        active={chat.active}
        loading={chat.loadingThreads}
        search={search}
        setSearch={setSearch}
        setActive={openThreadAtIdx}
        contentHits={contentHits}
        contentSearching={contentSearching}
        onNewThread={() => { chat.newThread(); setSideOpen(false); notify("New thread"); }}
        onDeleteThread={async (id) => {
          try { await chat.deleteThread(id); notify("Chat deleted"); }
          catch (err: any) { notify(err?.message || "Delete failed"); }
        }}
        onClose={() => setSideOpen(false)}
        onOpenMenu={() => setMenuOpen(true)}
        dense={tweaks.sidebarDense}
        me={me}
        model={env.model}
      />

      <section className="chat-main">
        <div className="chat-head">
          <button
            className="chat-title-btn"
            onClick={() => setSideOpen(true)}
            title="Switch thread"
          >
            <div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <h2
                  style={{
                    margin: 0,
                    fontFamily: "var(--f-display)",
                    fontWeight: 600,
                    fontSize: 18,
                    letterSpacing: "-0.01em",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  {headerTitle}
                </h2>
                <span className="chat-title-chev" aria-hidden>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </span>
              </div>
              <div
                style={{
                  display: "flex", gap: 8, marginTop: 4,
                  fontSize: 11.5,
                  color: "var(--text-dim)",
                  fontFamily: "var(--f-mono)",
                  alignItems: "center", flexWrap: "wrap",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>{env.model}</span>
                <span>·</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  mode: <span style={{ color: "var(--accent)" }}>{MODE_LABEL[mode]}</span>
                </span>
                {headerUpdated && (
                  <>
                    <span className="desk-only-inline">·</span>
                    <span className="desk-only-inline" style={{ whiteSpace: "nowrap" }}>
                      {headerUpdated}
                    </span>
                  </>
                )}
              </div>
            </div>
          </button>

          <ModePicker mode={mode} setMode={setMode} />
          <button className="icon-btn desk-only-inline" title="Share">
            <Icon name="share" />
          </button>
          <button className="icon-btn" onClick={() => setMenuOpen(true)} title="Chat actions">
            <Icon name="more" />
          </button>
        </div>

        <div className="chat-messages" ref={messagesRef}>
          {chat.loadingMessages && (
            <p style={{ color: "var(--text-dim)", fontSize: 12, textAlign: "center" }}>
              loading…
            </p>
          )}
          {!chat.loadingMessages && chat.messages.length === 0 && (
            <EmptyChat onTry={(p) => setDraft(p)} suggestions={MOCK_SUGGESTIONS} />
          )}
          {/* Render the interleaved items list (persisted bubbles +
            * tool groups, in conversation order). Compaction markers
            * are placed by ATINDEX which counts only message rows, so
            * we track a separate counter that increments only on
            * "msg" items. */}
          {(() => {
            let msgIdx = 0;
            return chat.items.map((item) => {
              if (item.kind === "tools") {
                return tweaks.showToolCalls
                  ? <ToolCallGroup key={item.id} calls={item.calls} />
                  : null;
              }
              if (item.kind === "reasoning") {
                return <ReasoningRow key={item.id} text={item.text} running={item.running} />;
              }
              const i = msgIdx++;
              const markers = chat.compactionMarkers.filter((cm) => cm.atIndex === i);
              return (
                <>
                  {markers.map((cm, idx) => (
                    <CompactionLine key={`cm-${i}-${idx}`} before={cm.before} after={cm.after} />
                  ))}
                  <Message key={item.msg.id} m={item.msg} bubble={tweaks.bubble} idx={i} />
                </>
              );
            });
          })()}
          {/* Live streaming overflow: any messages added by the token
            * stream that aren't yet in `items` (items is rebuilt from
            * server truth on agent:done; during streaming, new
            * assistant text is appended to `messages` only). */}
          {/* Live streaming overflow: show the assistant reply as soon as
            * tokens arrive, then keep reasoning/tool rows underneath.
            * This prevents the visible answer from appearing below live
            * debug activity in long tool-heavy turns.
            *
            * Reasoning is gated on chat.sending (not chat.thinking)
            * because thinking flips off as soon as the first content
            * token arrives, but reasoning should stay visible until the
            * turn fully completes (then the persisted version takes over
            * via chat.items). */}
          {(() => {
            const persistedMsgCount = chat.items.filter((it) => it.kind === "msg").length;
            const live = chat.messages.slice(persistedMsgCount);
            return (
              <>
                {live.map((m) => (
                  <Message key={m.id} m={m} bubble={tweaks.bubble} />
                ))}
                {chat.sending && chat.liveReasoning.length > 0 && (
                  <ReasoningRow text={chat.liveReasoning} running={true} />
                )}
                {tweaks.showToolCalls && chat.toolCalls.length > 0 && (
                  <ToolCallGroup calls={chat.toolCalls} />
                )}
              </>
            );
          })()}
          {/* Markers anchored past the last message (e.g. compact + no
              new send yet) — render after all bubbles. */}
          {chat.compactionMarkers
            .filter((cm) => cm.atIndex >= chat.messages.length)
            .map((cm, idx) => (
              <CompactionLine key={`cm-tail-${idx}`} before={cm.before} after={cm.after} />
            ))}
          {chat.thinking && !chat.pendingApproval && !chat.pendingQuestion && (
            <ThinkingBubble bubble={tweaks.bubble} />
          )}
          {chat.compacting && <CompactingBanner />}
          {chat.pendingApproval && (
            <ApprovalCard
              pending={chat.pendingApproval}
              onRespond={(approved, reason) => chat.respondToApproval(approved, reason)}
            />
          )}
          {chat.pendingQuestion && (
            <AskCard
              pending={chat.pendingQuestion}
              onRespond={(answer) => chat.respondToAsk(answer)}
            />
          )}
        </div>

        <div className="chat-composer-wrap">
          <ChatComposer
            draft={draft}
            setDraft={setDraft}
            send={onSend}
            disabled={chat.sending || chat.compacting || !!chat.pendingApproval || !!chat.pendingQuestion}
            notify={notify}
          />
        </div>
      </section>

      <ChatActionsMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        mode={mode}
        setMode={setMode}
        activePresetName={chat.activePreset?.name ?? null}
        onManageSkills={() => setSkillsOpen(true)}
        onManageMemory={() => setMemoryOpen(true)}
        onManagePresets={() => setPresetsOpen(true)}
        onClearChat={() => {
          chat.clearLocal();
          notify("Local view cleared (server-side history retained)");
        }}
        onDeleteThread={async () => {
          if (!chat.active) {
            // Nothing saved on the server yet — just clear the local view.
            chat.clearLocal();
            notify("Cleared");
            return;
          }
          const ok = await confirm({
            title: "Delete this chat?",
            message: "The thread and its message history will be removed. This can't be undone.",
            confirmLabel: "Delete chat",
            danger: true,
          });
          if (!ok) return;
          try { await chat.deleteThread(chat.active); notify("Chat deleted"); }
          catch (err: any) { notify(err?.message || "Delete failed"); }
        }}
        onCompact={async () => {
          const r = await chat.compact();
          if (r) notify(`Context compacted: ${r.before} → ${r.after} turns`);
        }}
        onShare={() => {
          if (!chat.active) {
            notify("Send a message first — sharing needs a saved session");
            return;
          }
          setShareOpen(true);
        }}
        onExport={() => {
          const md = chat.exportMarkdown();
          if (!md) { notify("Nothing to export yet"); return; }
          const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          const stamp = new Date().toISOString().slice(0, 10);
          const threadName = chat.threads.find((t) => t.id === chat.active)?.name || "conversation";
          const safeName = threadName.replace(/[^\w\s-]+/g, "").trim().replace(/\s+/g, "-").slice(0, 60);
          a.href = url;
          a.download = `${stamp}-${safeName || "conversation"}.md`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }}
        notify={notify}
        me={me}
        model={env.model}
        cwd={env.cwd || "—"}
      />

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sessionId={chat.active}
        notify={notify}
      />

      <SkillsPanel
        open={skillsOpen}
        onClose={() => setSkillsOpen(false)}
      />

      <MemoryPanel
        open={memoryOpen}
        onClose={() => setMemoryOpen(false)}
        notify={notify}
      />

      <PresetsPanel
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        notify={notify}
        activePresetName={chat.activePreset?.name ?? null}
        onApply={(p) => chat.setActivePreset({ name: p.name, body: p.body })}
        onClear={() => chat.setActivePreset(null)}
      />
    </div>
  );
}

// Live status row shown at the bottom of the messages stream while a
// compaction RPC is in flight. Blocks the composer (handled at the
// parent) so the user can't fire a new turn against half-built
// context. Auto-disappears when compact() resolves.
function CompactingBanner() {
  return (
    <div className="compacting-banner" role="status" aria-live="polite">
      <span className="compacting-banner-spinner" aria-hidden />
      <div>
        <div className="compacting-banner-title">Compacting context…</div>
        <div className="compacting-banner-sub">
          Older turns are being summarized. The chat will unlock when this finishes.
        </div>
      </div>
    </div>
  );
}

// Inline marker dropped into the message stream at the point where
// the LLM context was truncated. The scrollback above this stays
// visible to the user; the agent's next turn only sees what's below
// (plus the synthetic summary that compaction produced server-side).
function CompactionLine({ before, after }: { before: number; after: number }) {
  return (
    <div className="compaction-line" role="separator">
      <span className="compaction-line-rule" />
      <span className="compaction-line-label">
        context compacted · {before} → {after} turns
      </span>
      <span className="compaction-line-rule" />
    </div>
  );
}

function ModePicker({ mode, setMode }: { mode: ChatMode; setMode: (m: ChatMode) => void }) {
  return (
    <div className="chat-mode-pill">
      {CHAT_MODES.map((m) => (
        <button
          key={m}
          data-active={mode === m}
          onClick={() => setMode(m)}
          title={MODE_DESCRIPTION[m]}
        >
          {MODE_LABEL[m]}
        </button>
      ))}
    </div>
  );
}

// Friendly relative time. Server returns ISO; the prototype showed
// "12m / 3h / yesterday / 2d / 1w" — match that here so the sidebar
// reads the same.
function formatRelative(iso: string): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(day / 365)}y`;
}
